import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FaEdit, FaTrash } from "react-icons/fa";
import api from "../api";
import AddRoute from "../map/components/AddRoute";
import UpdateSettingsModal from "./components/UpdateRoute";

// Define all possible event types
export const EventTypeOptions = [
  "Astronomical Low Tide",
  "Avalanche",
  "Blizzard",
  "Coastal Flood",
  "Cold/Wind Chill",
  "Debris Flow",
  "Dense Fog",
  "Dense Smoke",
  "Drought",
  "Dust Devil",
  "Dust Storm",
  "Excessive Heat",
  "Extreme Cold/Wind Chill",
  "Flash Flood",
  "Flood",
  "Freezing Fog",
  "Frost/Freeze",
  "Funnel Cloud",
  "Hail",
  "Heat",
  "Heavy Rain",
  "Heavy Snow",
  "High Surf",
  "High Wind",
  "Hurricane",
  "Ice Storm",
  "Lake-Effect Snow",
  "Lakeshore Flood",
  "Lightning",
  "Marine Dense Fog",
  "Marine Hail",
  "Marine High Wind",
  "Marine Hurricane/Typhoon",
  "Marine Lightning",
  "Marine Strong Wind",
  "Marine Thunderstorm Wind",
  "Marine Tropical Depression",
  "Marine Tropical Storm",
  "Rip Current",
  "Seiche",
  "Sleet",
  "Sneakerwave",
  "Storm Surge/Tide",
  "Strong Wind",
  "Thunderstorm Wind",
  "Tornado",
  "Tropical Depression",
  "Tropical Storm",
  "Volcanic Ashfall",
  "Waterspout",
  "Wildfire",
  "Winter Storm",
  "Winter Weather",
];

const SettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState<any[]>([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState("routes"); // "routes" or "preferences"
  const [selectedSubTab, setSelectedSubTab] = useState("weatherType"); // Default tab in preferences
  const [addRouteVisible, setAddRouteVisible] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        setRoutes(data.routes || []);
        if (!data.userSettings?.event_types || data.userSettings.event_types.length === 0) {
          setSelectedEventTypes([...EventTypeOptions]);
        } else {
          setSelectedEventTypes(data.userSettings.event_types);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        toast.error("Error fetching settings");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleEventTypeChange = (eventType: string) => {
    setSelectedEventTypes((prevSelected) =>
      prevSelected.includes(eventType)
        ? prevSelected.filter((type) => type !== eventType)
        : [...prevSelected, eventType]
    );
    setIsChanged(true);
  };

  // Submit updated user settings
  const handleSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/settings/update-user", {
        event_types: selectedEventTypes,
      });
      toast.success("User settings updated");
    } catch (error) {
      console.error("Error updating user settings:", error);
      toast.error("Error updating user settings");
    }
  };

  const handleDeleteRoute = async (routeId: number) => {
    try {
      await api.delete(`settings/${routeId}`);
      toast.success("Route deleted");
      setRoutes(routes.filter((r) => r.route_id !== routeId));
    } catch (error) {
      console.error("Error deleting route:", error);
      toast.error("Error deleting route");
    }
  };

  // Navigate to edit route details page
  const handleEditRoute = async (routeId: number) => {
    setSelectedRouteId(routeId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRouteId(null);
  };

  const handleUpdated = (updatedRoute: any) => {
    setRoutes((prevRoutes) =>
      prevRoutes.map((route) =>
        route.route_id === updatedRoute.route_id ? updatedRoute : route
      )
    );
    closeModal();
  };

  const handleSaveRoute = (formData: any) => {
    setRoutes([...routes, formData]);
    setAddRouteVisible(false);
  };

  const handleAddRouteClose = () => {
    setAddRouteVisible(false);
  };

  const selectAllEventTypes = () => {
    setSelectedEventTypes([...EventTypeOptions]);
    setIsChanged(true);
  };

  const deselectAllEventTypes = () => {
    setSelectedEventTypes([]);
    setIsChanged(true);
  };

  const handleSaveSettings = async () => {
    try {
      await api.post("/settings/update-user", { event_types: selectedEventTypes });
      toast.success("User settings updated");
      setIsChanged(false);
    } catch (error) {
      console.error("Error updating user settings:", error);
      toast.error("Error updating user settings");
    }
  };


  if (loading) {
    return <div className="p-6 text-center">Loading settings...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6 mt-8">
      <h1 className="text-3xl font-bold mb-6">Settings</h1>

      {/* Horizontal Tabs */}
      <div className="bg-white shadow rounded p-6">
        <div className="flex border-b mb-4">
          <button
            className={`px-6 py-2 ${selectedTab === "routes" ? "border-b-2 border-blue-600 font-semibold" : "text-gray-500"}`}
            onClick={() => setSelectedTab("routes")}
          >
            Route Settings
          </button>
          <button
            className={`px-6 py-2 ${selectedTab === "preferences" ? "border-b-2 border-blue-600 font-semibold" : "text-gray-500"}`}
            onClick={() => setSelectedTab("preferences")}
          >
            User Preferences
          </button>
        </div>

        {/* Route Management */}
        {selectedTab === "routes" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">Manage Routes</h2>
              <button
                onClick={() => setAddRouteVisible(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                Add New Route
              </button>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Location
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Location
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {routes.map((route) => (
                  <tr key={route.route_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {route.r_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {route.start_loc_name ?? "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {route.end_loc_name ?? "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                      <button
                        onClick={() => handleEditRoute(route.route_id)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteRoute(route.route_id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {routes.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                No routes found.
              </div>
            )}
          </div>
        )}
        {selectedRouteId !== null && (
          <UpdateSettingsModal
            routeId={selectedRouteId}
            isOpen={isModalOpen}
            onClose={closeModal}
            onUpdated={handleUpdated}
          />
        )}

        {/* User Preferences with Vertical Tabs */}
        {selectedTab === "preferences" && (
          <div className="flex">
            <div className="w-1/4 border-r pr-4">
              <button
                className={`block w-full text-left px-4 py-2 ${selectedSubTab === "weatherType" ? "bg-blue-100 font-semibold" : "text-gray-700"}`}
                onClick={() => setSelectedSubTab("weatherType")}
              >
                Weather Type Selection
              </button>
            </div>
            <div className="w-3/4 pl-6">
              {selectedSubTab === "weatherType" ? (
                <><div className="max-h-64 overflow-y-auto border p-2 rounded mb-4">
                  {EventTypeOptions.map((eventType) => (
                    <label key={eventType} className="flex items-center gap-2 p-2 cursor-pointer">
                      <input type="checkbox" checked={selectedEventTypes.includes(eventType)} onChange={() => handleEventTypeChange(eventType)} className="w-4 h-4" />
                      {eventType}
                    </label>
                  ))}
                </div>
                  <div className="flex gap-x-2 mb-4">
                    <button onClick={selectAllEventTypes} className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600">Select All</button>
                    <button onClick={deselectAllEventTypes} className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600">Deselect All</button>
                  </div>
                  {isChanged && (
                    <button onClick={handleSaveSettings} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-800">Save Changes</button>
                  )}</>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        )}
      </div>

      <AddRoute
        isVisible={addRouteVisible}
        onClose={handleAddRouteClose}
        onSave={handleSaveRoute}
      />
    </div>
  );
};

export default SettingsPage;
